import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaCoins, FaArrowUp, FaArrowDown, FaGem, FaCrown, FaRocket, FaBolt, FaCheckCircle, FaHistory } from 'react-icons/fa';
import { walletAPI } from '../utils/api';
import { toast } from 'sonner';
import { useSearchParams, Link } from 'react-router-dom';
import { getAvatarUrl } from '../utils/avatar';
import Sidebar from '../components/Sidebar';

const PACKAGE_ICONS = { starter: FaCoins, popular: FaGem, pro: FaCrown, elite: FaRocket };
const PACKAGE_COLORS = {
  starter: 'from-blue-500 to-cyan-400',
  popular: 'from-purple-500 to-pink-500',
  pro: 'from-amber-500 to-orange-500',
  elite: 'from-emerald-500 to-teal-400',
};

const Wallet = () => {
  const [balance, setBalance] = useState(0);
  const [walletData, setWalletData] = useState({});
  const [packages, setPackages] = useState([]);
  const [transactions, setTransactions] = useState({ purchases: [], tips_sent: [], tips_received: [] });
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [activeTab, setActiveTab] = useState('packages');
  const [searchParams, setSearchParams] = useSearchParams();

  const loadWallet = useCallback(async () => {
    try {
      const [balRes, pkgRes, txnRes] = await Promise.all([
        walletAPI.getBalance(),
        walletAPI.getPackages(),
        walletAPI.getTransactions(),
      ]);
      setBalance(balRes.data.balance);
      setWalletData(balRes.data);
      setPackages(pkgRes.data);
      setTransactions(txnRes.data);
    } catch (err) {
      console.error('Failed to load wallet', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWallet(); }, [loadWallet]);

  // Poll for payment status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        toast.error('Payment status check timed out. Please refresh.');
        setSearchParams({});
        return;
      }
      try {
        const res = await walletAPI.checkStatus(sessionId);
        if (res.data.status === 'paid') {
          toast.success(`Payment successful! +${res.data.points} PointPhoria added!`);
          setSearchParams({});
          loadWallet();
          return;
        }
        if (res.data.status === 'expired') {
          toast.error('Payment expired. Please try again.');
          setSearchParams({});
          return;
        }
        attempts++;
        setTimeout(poll, 2000);
      } catch {
        attempts++;
        setTimeout(poll, 2000);
      }
    };

    toast.loading('Verifying payment...', { id: 'payment-check' });
    poll().then(() => toast.dismiss('payment-check'));
  }, [searchParams, setSearchParams, loadWallet]);

  const handlePurchase = async (pkgId) => {
    setPurchasing(pkgId);
    try {
      const originUrl = window.location.origin;
      const res = await walletAPI.purchase(pkgId, originUrl);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
      setPurchasing(null);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="grid grid-cols-1 md:grid-cols-12"><Sidebar /><main className="col-span-12 md:col-span-10 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#4A90E2] border-t-transparent rounded-full animate-spin" /></main></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="wallet-page">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        <main className="col-span-12 md:col-span-10">
          <div className="max-w-3xl mx-auto p-6 md:p-10">
            {/* Balance Card */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 mb-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-amber-400/20 to-transparent rounded-full -translate-y-10 translate-x-10" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-purple-500/20 to-transparent rounded-full translate-y-10 -translate-x-10" />
              <div className="relative">
                <p className="text-gray-400 text-sm font-medium mb-1">Your Balance</p>
                <div className="flex items-baseline space-x-2 mb-4">
                  <span className="text-5xl font-bold tracking-tight" data-testid="wallet-balance">{balance.toLocaleString()}</span>
                  <span className="text-amber-400 font-bold text-lg font-arthaus">PP</span>
                </div>
                <p className="text-amber-400 font-semibold text-sm font-arthaus">PointPhoria</p>
                <div className="flex items-center gap-6 mt-4 text-sm text-gray-400">
                  <span className="flex items-center gap-1"><FaArrowDown className="text-green-400" size={10} /> Earned: {walletData.total_earned?.toLocaleString() || 0}</span>
                  <span className="flex items-center gap-1"><FaArrowUp className="text-red-400" size={10} /> Spent: {walletData.total_spent?.toLocaleString() || 0}</span>
                </div>
              </div>
            </motion.div>

            {/* Tabs */}
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-full mb-8">
              {[{ key: 'packages', label: 'Buy PointPhoria', icon: FaCoins }, { key: 'history', label: 'History', icon: FaHistory }].map(t => (
                <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`wallet-tab-${t.key}`} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-medium transition-all ${activeTab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  <t.icon size={14} /><span className={t.key === 'packages' ? 'font-arthaus' : ''}>{t.label}</span>
                </button>
              ))}
            </div>

            {activeTab === 'packages' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="packages-grid">
                {packages.map((pkg, i) => {
                  const Icon = PACKAGE_ICONS[pkg.id] || FaCoins;
                  const gradient = PACKAGE_COLORS[pkg.id] || 'from-gray-500 to-gray-600';
                  return (
                    <motion.div key={pkg.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-all group" data-testid={`package-${pkg.id}`}>
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4`}>
                        <Icon className="text-white" size={20} />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{pkg.label}</h3>
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-3xl font-bold text-gray-900">{pkg.points.toLocaleString()}</span>
                        <span className="text-amber-500 font-semibold text-sm font-arthaus">PP</span>
                      </div>
                      {pkg.bonus && <span className="inline-block bg-green-50 text-green-600 text-xs font-semibold px-2 py-0.5 rounded-full mb-3">{pkg.bonus}</span>}
                      <button onClick={() => handlePurchase(pkg.id)} disabled={purchasing === pkg.id} data-testid={`buy-${pkg.id}`} className={`w-full mt-3 py-2.5 rounded-xl font-semibold text-sm transition-all ${purchasing === pkg.id ? 'bg-gray-200 text-gray-400' : `bg-gradient-to-r ${gradient} text-white hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]`}`}>
                        {purchasing === pkg.id ? 'Redirecting...' : `$${pkg.price}`}
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-6">
                {/* Purchases */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Purchases</h3>
                  {transactions.purchases.length === 0 ? (
                    <p className="text-gray-400 text-sm bg-white rounded-xl p-4 border border-gray-100">No purchases yet</p>
                  ) : (
                    <div className="space-y-2">
                      {transactions.purchases.map(t => (
                        <div key={t.id} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${t.payment_status === 'paid' ? 'bg-green-50' : 'bg-gray-100'}`}>
                              {t.payment_status === 'paid' ? <FaCheckCircle className="text-green-500" size={14} /> : <FaCoins className="text-gray-400" size={14} />}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">+{t.points} <span className="font-arthaus">PP</span></p>
                              <p className="text-xs text-gray-400">{formatDate(t.created_at)}</p>
                            </div>
                          </div>
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${t.payment_status === 'paid' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>${t.amount}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tips Received */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Tips Received</h3>
                  {transactions.tips_received.length === 0 ? (
                    <p className="text-gray-400 text-sm bg-white rounded-xl p-4 border border-gray-100">No tips received yet</p>
                  ) : (
                    <div className="space-y-2">
                      {transactions.tips_received.map(t => (
                        <div key={t.id} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Link to={`/profile/${t.sender_username}`} className="flex-shrink-0">
                              <img src={getAvatarUrl(t.sender_avatar, t.sender_name || t.sender_username)} alt={t.sender_name} className="w-10 h-10 rounded-full object-cover hover:ring-2 hover:ring-amber-400 transition-all" />
                            </Link>
                            <div>
                              <p className="text-sm font-medium text-gray-900">+{t.creator_amount} <span className="font-arthaus">PP</span></p>
                              <Link to={`/profile/${t.sender_username}`} className="text-xs text-[#4A90E2] hover:underline font-medium">from @{t.sender_username}</Link>
                              <p className="text-xs text-gray-400">{formatDate(t.created_at)}</p>
                            </div>
                          </div>
                          <span className="text-xs text-gray-400">{t.target_type}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tips Sent */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Tips Sent</h3>
                  {transactions.tips_sent.length === 0 ? (
                    <p className="text-gray-400 text-sm bg-white rounded-xl p-4 border border-gray-100">No tips sent yet</p>
                  ) : (
                    <div className="space-y-2">
                      {transactions.tips_sent.map(t => (
                        <div key={t.id} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Link to={`/profile/${t.recipient_username}`} className="flex-shrink-0">
                              <img src={getAvatarUrl(t.recipient_avatar, t.recipient_name || t.recipient_username)} alt={t.recipient_name} className="w-10 h-10 rounded-full object-cover hover:ring-2 hover:ring-purple-400 transition-all" />
                            </Link>
                            <div>
                              <p className="text-sm font-medium text-gray-900">-{t.amount} <span className="font-arthaus">PP</span></p>
                              <Link to={`/profile/${t.recipient_username}`} className="text-xs text-[#4A90E2] hover:underline font-medium">to @{t.recipient_username}</Link>
                              <p className="text-xs text-gray-400">{formatDate(t.created_at)}</p>
                            </div>
                          </div>
                          <span className="text-xs text-gray-400">{t.target_type}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Platform fee note */}
            <p className="text-center text-xs text-gray-400 mt-8">TalkinPoint takes a 16% platform fee on all tips. Creators receive 84% of every tip.</p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Wallet;
